/**
 * The journal of a report: read-only, since the server writes it as reports save.
 *
 * The trail of the whole report carries no content; the history of one block
 * does, so that two of its states can be compared.
 */

import { http, type RequestOptions } from '@/api/http'
import { readEntry, type JournalEntry, type StoredEntry } from '@/domain/journal'

interface Envelope {
  changes: StoredEntry[]
}

export interface Page {
  /** Entries older than this id: how a trail is read further back. */
  readonly before?: number
  readonly limit?: number
}

function query(page: Page): string {
  const params = new URLSearchParams()
  if (page.before !== undefined) params.set('before', String(page.before))
  if (page.limit !== undefined) params.set('limit', String(page.limit))
  const text = params.toString()
  return text ? `?${text}` : ''
}

/** Who changed what in the report, the newest first. */
export async function listJournal(
  draftId: number,
  page: Page = {},
  options: RequestOptions = {},
): Promise<JournalEntry[]> {
  const { changes } = await http.get<Envelope>(
    `/reportDrafts/${draftId}/journal${query(page)}`,
    options,
  )
  return changes.map(readEntry)
}

/** What one block said at each change, the newest first. */
export async function blockHistory(
  draftId: number,
  sectionId: string,
  page: Page = {},
  options: RequestOptions = {},
): Promise<JournalEntry[]> {
  const { changes } = await http.get<Envelope>(
    `/reportDrafts/${draftId}/journal/${encodeURIComponent(sectionId)}${query(page)}`,
    options,
  )
  return changes.map(readEntry)
}
